// @flow
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { NavigationUtil } from 'utils/NavigationUtil';
import { RouteNames } from 'router';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { PageLoader } from 'components/molecules/pageLoader';
import { ContactUsSection } from 'components/templates/contactUsSection';
import FooterSection from 'components/templates/footer';
import PageTemplate from 'components/templates/pageTemplate';
import { useViewport } from 'components/templates/viewportProvider';
import type { AppState } from 'modules/rootReducer';
import type { Contact, SiteSettings, Vertical } from 'types/common';
import LoyaltyBanner from 'components/organisms/loyaltyBanner';
import { isEmpty } from 'lodash';
import VisionPhilosophySection from 'components/templates/vision&PhilosophySection';
import { CmsPagesActions } from 'modules/cmsPages/actions';
import { CmsPagesSelector } from 'modules/cmsPages/selector';
import { FooterSelector } from 'modules/footerLink/selector';
import { FooterActions } from 'modules/footerLink/actions';

type Props = {
  isLoading: boolean,
  verticals: Array<Vertical>,
  verticalId: number | null,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
  contact: Contact,
  getContactDetails: () => void,
};

const LoyaltyProgramPage = (props: Props) => {
  const {
    isLoading,
    getVerticals,
    verticals,
    verticalId,
    getSiteSettings,
    siteSettings,
    visionPhilosophySection,
    contact,
    getPageData,
    getContactDetails,
    footerLink,
    getFooterLinkData,
    getBannerData,
    loyaltyBanner,
  } = props;

  const { isMobile } = useViewport();
  let [theme, setTheme] = useState(isMobile ? 'dark-theme' : 'light-theme');

  useEffect(() => {
    getBannerData();
    getContactDetails();
    getVerticals();
    getSiteSettings();
    getPageData();
    getFooterLinkData();
  }, []);

  if (isLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  if (
    !loyaltyBanner ||
    !contact ||
    !siteSettings ||
    !verticals ||
    !footerLink ||
    !visionPhilosophySection
  ) {
    return null;
  }

  const componentList = [
    ...(!isEmpty(loyaltyBanner)
      ? [
          {
            component: LoyaltyBanner,
            data: { loyaltyBanner },
            theme: isMobile ? 'dark-theme' : 'light-theme',
            sectionId: 'banner',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Loyalty Program',
                breadcrumbLink: RouteNames.loyaltyProgram,
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(visionPhilosophySection.visionSection)
      ? [
          {
            component: VisionPhilosophySection,
            data: { visionPhilosophySection },
            mobileBannerData: visionPhilosophySection.visionSectionMobile,
            theme: 'light-theme',
            sectionId: 'vision-and-philosophy',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Loyalty Program',
                breadcrumbLink: RouteNames.loyaltyProgram,
              },
              {
                breadcrumbName: 'Vision & Philosophy',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    {
      component: ContactUsSection,
      data: {
        contact,
        verticals,
        verticalId,
        showVerticalDropdown: false,
        projectName: 'Loyalty Program',
        type: visionPhilosophySection.visionSectionMobile && 'no-padding',
      },
      location: props,
      sectionId: 'contact',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Loyalty Program',
          breadcrumbLink: RouteNames.loyaltyProgram,
        },
        {
          breadcrumbName: 'Contact Us',
          breadcrumbLink: '#',
        },
      ],
      theme: 'light-theme',
    },
    {
      component: FooterSection,
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Loyalty Program',
          breadcrumbLink: RouteNames.loyaltyProgram,
        },
        {
          breadcrumbName: 'Links',
          breadcrumbLink: '#',
        },
      ],
      data: { siteSettings, footerLink },
    },
  ];

  return (
    <PageTemplate
      theme={theme}
      componentList={componentList}
      updateTheme={e => setTheme(e)}
      verticals={verticals}
      siteSettings={siteSettings}
    />
  );
};

const mapStateToProps = (state: AppState, props: any) => {
  const verticalKey = NavigationUtil.getVerticalKeyForLocation(props.location);

  return {
    isLoading:
      HomeSelector.isLoading(state) ||
      CmsPagesSelector.isCmsPageLoading(state) ||
      FooterSelector.isLoading(state),
    loyaltyBanner: HomeSelector.getBanners(state, false, '', 'Loyalty Program'),
    contact: HomeSelector.getContactForMenuItems(state, 'Loyalty Program'),
    verticalId: HomeSelector.getVerticalIdForKey(verticalKey),
    verticals: HomeSelector.getVerticals(state),
    footerLink: FooterSelector.getFooterData(state),
    visionPhilosophySection: CmsPagesSelector.getVisionPhilosophy(
      state,
      'loyalty',
      '',
      false
    ),
    siteSettings: HomeSelector.getSiteSettings(state),
  };
};

const mapDispatchToProps = dispatch => {
  const {
    getBannerData,
    getContactDetails,
    getVerticals,
    getSiteSettings,
  } = HomeActions;
  const { getPageData } = CmsPagesActions;
  const { getFooterLinkData } = FooterActions;
  return bindActionCreators(
    {
      getBannerData,
      getContactDetails,
      getVerticals,
      getPageData,
      getFooterLinkData,
      getSiteSettings,
    },
    dispatch
  );
};
export default connect(mapStateToProps, mapDispatchToProps)(LoyaltyProgramPage);
